/**
 * TREATX-SOFT-LAUNCH-INVITE-ADMIN-UI-01
 * =====================================
 *
 * Admin UI for the existing soft-launch invite-key SSOT. This page
 * does NOT introduce any parallel state — every action calls the
 * canonical endpoints under `/api/admin/soft-launch-invite-keys/*`
 * which were already in production. The cleanup endpoints carry a
 * hard `source=soft_launch_invite` safety pin server-side; this UI
 * relies on that pin and does not duplicate the check client-side.
 *
 * Admin nav path: /#/treatx-admin/soft-launch-invites
 */
import React, { useCallback, useEffect, useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Copy, Check, ArrowLeft, Ticket, Trash2, Archive, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// TREATX-INVITE-ROLE-ENFORCEMENT-01 + TREATX-CANONICAL-ROLES-CLEANUP-01
// — UI hardening companion. Canonical invite roles are patient /
// provider / clinic_admin. `reviewer` is NOT a canonical TreatX RBAC
// role and has been removed from the invite role model. The `any`
// role bypasses ALL signup deny prefixes (gate-side matrix:
// _SIGNUP_ROLE_MATRIX in middleware/soft_launch_gate_middleware.py)
// and is intended for super-admin / internal QA only — it is hidden
// from non-super-admin admins so an ops user cannot accidentally
// mint an over-privileged invite key.
const ROLES_BASE = ['patient', 'provider', 'clinic_admin'];
const ROLE_ANY = 'any';

// TREATX-OWNER-ACCEPTANCE-P0-INVITE-LOGIN-SEARCH-01 —
// Plaintext claim codes are returned by the server EXACTLY ONCE at
// creation (POST /admin/soft-launch-invite-keys → `code`) and are NEVER
// recoverable from the DB. To keep them usable within the current
// admin browser session (so the owner can copy/re-copy without
// re-creating the invite) we cache plaintext codes in sessionStorage,
// keyed by `key_id`. The cache is per-tab and cleared on tab close —
// it does not extend the canonical security guarantee (one-time
// reveal), it just smooths the owner-UAT workflow.
const PLAINTEXT_CACHE_KEY = 'treatx_invite_plaintext_codes_v1';

const readPlaintextCache = () => {
  try {
    const raw = sessionStorage.getItem(PLAINTEXT_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_e) { return {}; }
};

const writePlaintextCache = (next) => {
  try {
    sessionStorage.setItem(PLAINTEXT_CACHE_KEY, JSON.stringify(next));
  } catch (_e) { /* sessionStorage may be unavailable; ignore */ }
};

const CopyButton = ({ value, label = 'Copy', copiedLabel = 'Copied', testid }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1400);
        } catch (_e) { /* clipboard unavailable; ignore */ }
      }}
      className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
      data-testid={testid || 'invite-copy-btn'}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? copiedLabel : label}
    </button>
  );
};

const KeyRow = ({ k, plaintext, onDeactivate, onShowDerived }) => {
  const expired = k.expires_at && new Date(k.expires_at).getTime() < Date.now();
  const exhausted = typeof k.max_uses === 'number' && k.used_count >= k.max_uses;
  const active = k.active && !expired && !exhausted;
  // TREATX-OWNER-ACCEPTANCE-P0-INVITE-CLAIM-NORMALIZE-01:
  // Plaintext is ONLY available immediately after creation (server
  // returns it once) or from the per-tab sessionStorage cache. If
  // we have it, the admin can copy the full `TX-XXXX-XXXX-XXXX-XXXX`
  // string. Otherwise we MUST NOT render any masked stand-in (e.g.
  // `•••• XXXX`) that owners mistake for the actual claim code and
  // try to paste — that was the root cause of the reported "claim
  // code length mismatch" + "4 chars missing" UAT confusion.
  const claimCode = k.code || plaintext;
  const statusReason = !k.active
    ? 'Deactivated by admin'
    : expired
      ? `Expired on ${k.expires_at ? formatDateTimeLocale(k.expires_at) : 'an earlier date'}`
      : exhausted
        ? `Used up (${k.used_count}/${k.max_uses})`
        : null;
  return (
    <tr data-testid={`invite-row-${k.key_id}`} className={`border-b border-slate-100 ${active ? '' : 'bg-slate-50/60 text-slate-400'}`}>
      <td
        className="px-3 py-2 font-mono text-[11px] text-slate-500 align-top"
        title="Internal reference only. Cannot be used for claiming."
        data-testid={`invite-keyid-${k.key_id}`}
      >
        <div className="flex flex-col gap-0.5">
          <span className="text-slate-500">{k.key_id}</span>
          <span
            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-slate-400 font-sans"
            data-testid={`invite-keyid-internal-tag-${k.key_id}`}
          >
            <span className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200">internal only</span>
            <span className="normal-case tracking-normal text-slate-400">Not a claim code.</span>
          </span>
        </div>
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        {claimCode ? (
          <div className="flex items-center gap-2">
            <span data-testid={`invite-code-${k.key_id}`} className="text-slate-900 select-all break-all">
              {claimCode}
            </span>
            <CopyButton
              value={claimCode}
              label="Copy code"
              copiedLabel="Copied"
              testid={`invite-copy-code-${k.key_id}`}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-0.5" data-testid={`invite-code-not-available-${k.key_id}`}>
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500">
              <span className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">not retrievable</span>
            </span>
            <span className="text-[10px] text-slate-400 font-sans normal-case">
              {statusReason
                ? statusReason
                : <>Plaintext shown once at creation. Last 4 chars (for audit only): <strong>{k.code_last4 || '—'}</strong></>}
            </span>
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-xs">{k.label || '—'}</td>
      <td className="px-3 py-2 text-xs">{k.allowed_role}</td>
      <td className="px-3 py-2 text-xs">{k.used_count}/{k.max_uses ?? '∞'}</td>
      <td className="px-3 py-2 text-xs">{k.expires_at ? formatDateTimeLocale(k.expires_at) : '—'}</td>
      <td className="px-3 py-2 text-xs">
        <span
          className={
            active
              ? 'px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium'
              : 'px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 font-medium'
          }
          data-testid={`invite-status-${k.key_id}`}
        >
          {active ? 'active' : (expired ? 'expired' : (exhausted ? 'exhausted' : 'inactive'))}
        </span>
      </td>
      <td className="px-3 py-2 text-xs text-slate-600">{k.created_by || '—'}</td>
      <td className="px-3 py-2 text-xs text-right space-x-2 whitespace-nowrap">
        <Button size="sm" variant="outline" onClick={() => onShowDerived(k)}
                data-testid={`invite-derived-${k.key_id}`}>
          Users
        </Button>
        {active && (
          <Button size="sm" variant="destructive" onClick={() => onDeactivate(k)}
                  data-testid={`invite-deactivate-${k.key_id}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </td>
    </tr>
  );
};

export default function SoftLaunchInviteKeysPage() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  // Only super-admins can mint `any`-role invite keys (matches the
  // gate-side matrix that lets `any` bypass every signup deny prefix).
  const canIssueAnyRole = (user?.role === 'treatx_super_admin');
  const ROLES = canIssueAnyRole ? [...ROLES_BASE, ROLE_ANY] : ROLES_BASE;
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    label: '',
    allowed_role: 'patient',
    max_uses: 10,
    // TREATX-OWNER-ACCEPTANCE-P0-INVITE-CLAIM-NORMALIZE-01 — bumped
    // default from 14 → 30 days. Owner UAT runs over multi-week
    // soft-prod windows; 14 days routinely expired before the
    // invitee got to test. Backend still enforces ge=1, le=365.
    ttl_days: 30,
  });
  const [lastCreated, setLastCreated] = useState(null);
  const [plaintextCache, setPlaintextCache] = useState(() => readPlaintextCache());
  const [derivedKey, setDerivedKey] = useState(null);
  const [derivedUsers, setDerivedUsers] = useState([]);
  const [derivedBusy, setDerivedBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/soft-launch-invite-keys');
      setKeys(data.keys || []);
    } catch (e) {
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const fetchDerived = useCallback(async (key) => {
    setDerivedBusy(true);
    setDerivedKey(key);
    try {
      const { data } = await api.get(
        `/admin/soft-launch-invite-keys/derived-users?key_id=${encodeURIComponent(key.key_id)}` +
        (showArchived ? '&include_archived=true' : ''),
      );
      setDerivedUsers(data.users || []);
    } catch (_) {
      setDerivedUsers([]);
    } finally {
      setDerivedBusy(false);
    }
  }, [showArchived]);

  const onCreate = async (e) => {
    e?.preventDefault?.();
    if (!form.label.trim()) {
      window.alert('Please enter a label so admins can identify this invite.');
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post('/admin/soft-launch-invite-keys', form);
      setLastCreated(data);
      // Cache plaintext code in sessionStorage so the admin can re-copy
      // it from the table within the current browser tab. The server
      // does NOT persist plaintext — this cache is per-tab, never
      // shared cross-device.
      const keyId = data?.key?.key_id;
      const plaintext = data?.code;
      if (keyId && plaintext) {
        const next = { ...readPlaintextCache(), [keyId]: plaintext };
        writePlaintextCache(next);
        setPlaintextCache(next);
      }
      await fetchKeys();
    } catch (err) {
      window.alert('Could not create: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setCreating(false);
    }
  };

  const onDeactivate = async (k) => {
    if (!window.confirm(`Deactivate invite "${k.label || k.key_id}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/soft-launch-invite-keys/${k.key_id}`);
      // Drop the plaintext cache entry — a deactivated key cannot be
      // claimed anyway, so keeping it would only confuse owner UAT.
      if (plaintextCache[k.key_id]) {
        const next = { ...plaintextCache };
        delete next[k.key_id];
        writePlaintextCache(next);
        setPlaintextCache(next);
      }
      await fetchKeys();
    } catch (err) {
      window.alert('Could not deactivate: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const onArchiveAll = async () => {
    if (!derivedKey) return;
    const n = derivedUsers.filter((u) => u.status !== 'archived_soft_launch').length;
    if (n === 0) return;
    if (!window.confirm(`Archive ${n} invite-created user(s)? Reversible. Real admin users are protected by the source=soft_launch_invite pin.`)) return;
    try {
      await api.post('/admin/soft-launch-invite-keys/archive-derived-users', { key_id: derivedKey.key_id });
      await fetchDerived(derivedKey);
    } catch (err) {
      window.alert('Could not archive: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const onUnarchiveOne = async (uid) => {
    try {
      await api.post('/admin/soft-launch-invite-keys/unarchive-derived-users', { user_ids: [uid] });
      await fetchDerived(derivedKey);
    } catch (err) {
      window.alert('Could not unarchive: ' + (err?.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 sm:px-8 py-6" data-testid="soft-launch-invites-page">
      <div className="max-w-6xl mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate('/treatx-admin/dashboard')}
                className="mb-3" data-testid="back-to-dashboard">
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Dashboard
        </Button>
        <div className="flex items-center gap-2 mb-1">
          <Ticket className="h-5 w-5 text-blue-700" />
          <h1 className="text-xl font-semibold text-slate-900">Soft Launch — Invitations</h1>
        </div>
        <p className="text-sm text-slate-500 mb-6">
          Create and manage invite codes. Codes carry a role and let invitees bypass the public-signup block while the
          soft-launch gate is on. Every invite-created user is stamped with <code>source=soft_launch_invite</code> for safe cleanup.
        </p>
        <div className="mb-6 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-900" data-testid="invite-id-vs-code-explainer">
          <strong>Reference ID</strong> (e.g. <code>ilk-...</code>) is an internal identifier used by audit logs &amp; cleanup tools — <em>never give it to an invitee</em>.
          The <strong>Claim code</strong> (e.g. <code>TX-XXXX-XXXX-XXXX-XXXX</code>, always <strong>22 characters</strong>) is the user-facing value the invitee types on the landing-page gate, and is shown <strong>once at creation</strong> only — it cannot be retrieved later from the database.
        </div>

        {/* Create */}
        <Card className="mb-6">
          <CardContent className="py-5">
            <h2 className="text-sm font-semibold text-slate-900 mb-3">Create invitation</h2>
            <form onSubmit={onCreate} className="grid grid-cols-1 sm:grid-cols-5 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-600 mb-1">Label</label>
                <Input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="e.g. Mehmet (clinic 12) — UAT"
                  data-testid="invite-form-label"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Role</label>
                <Select value={form.allowed_role} onValueChange={(v) => setForm({ ...form, allowed_role: v })}>
                  <SelectTrigger data-testid="invite-form-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Max uses</label>
                <Input
                  type="number"
                  min="1"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: parseInt(e.target.value || '1', 10) })}
                  data-testid="invite-form-max-uses"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Expires in (days)</label>
                <Input
                  type="number"
                  min="1"
                  value={form.ttl_days}
                  onChange={(e) => setForm({ ...form, ttl_days: parseInt(e.target.value || '1', 10) })}
                  data-testid="invite-form-ttl"
                />
              </div>
              <div className="sm:col-span-5 flex justify-end">
                <Button type="submit" disabled={creating} data-testid="invite-form-submit">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create invite'}
                </Button>
              </div>
            </form>
            {lastCreated?.code && (
              <div className="mt-4 bg-amber-50 border border-amber-300 rounded-md p-3 text-xs" data-testid="invite-last-created">
                <p className="font-semibold text-amber-900 mb-1">
                  New invite ready — record this <span className="underline">claim code</span> now.
                </p>
                <p className="text-amber-800 mb-2 text-[11px]">
                  This is the value the invitee types on the landing-page gate.
                  It is shown ONCE — the database only retains a hashed copy.
                  <strong className="ml-1">Do not share the Reference ID below — it is internal only.</strong>
                </p>
                <div className="flex items-center gap-3 font-mono text-sm">
                  <span data-testid="invite-last-created-code" className="select-all break-all">{lastCreated.code}</span>
                  <CopyButton value={lastCreated.code} label="Copy claim code" />
                </div>
                <div className="mt-1 text-[10px] text-amber-700 font-mono" data-testid="invite-last-created-length">
                  Length: <strong>{lastCreated.code.length}</strong> characters (canonical claim codes are exactly 22 characters — verify before sharing).
                </div>
                {lastCreated?.key?.key_id && (
                  <div className="mt-2 text-[10px] text-slate-500" data-testid="invite-last-created-keyid">
                    Reference ID (internal, not for invitees): {lastCreated.key.key_id}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* List */}
        <Card>
          <CardContent className="py-4 overflow-x-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-900">Existing invitations</h2>
              <Button variant="outline" size="sm" onClick={fetchKeys} data-testid="invite-refresh">
                Refresh
              </Button>
            </div>
            {loading ? (
              <div className="text-center py-8 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
            ) : keys.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">No invitations yet. Create one above.</p>
            ) : (
              <table className="min-w-full text-left" data-testid="invite-table">
                <thead className="text-xs text-slate-500 uppercase">
                  <tr>
                    <th className="px-3 py-2">Reference ID (internal)</th>
                    <th className="px-3 py-2">Claim code (share with invitee)</th>
                    <th className="px-3 py-2">Label</th>
                    <th className="px-3 py-2">Role</th>
                    <th className="px-3 py-2">Uses</th>
                    <th className="px-3 py-2">Expires</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Created by</th>
                    <th className="px-3 py-2 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <KeyRow
                      key={k.key_id}
                      k={k}
                      plaintext={plaintextCache[k.key_id]}
                      onDeactivate={onDeactivate}
                      onShowDerived={fetchDerived}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Derived users panel */}
        {derivedKey && (
          <Card className="mt-6">
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">
                    Users created by invitation:{' '}
                    <span className="text-slate-600 font-mono text-xs">{derivedKey.label || derivedKey.key_id}</span>
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Cleanup uses the canonical source-pin (only affects rows with <code>source=soft_launch_invite</code>; real admin users are mathematically protected).
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={showArchived}
                      onChange={(e) => { setShowArchived(e.target.checked); }}
                      data-testid="invite-show-archived"
                    />
                    Show archived
                  </label>
                  <Button variant="outline" size="sm" onClick={() => fetchDerived(derivedKey)}
                          data-testid="invite-derived-refresh">
                    {derivedBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Refresh'}
                  </Button>
                  <Button variant="destructive" size="sm" onClick={onArchiveAll}
                          data-testid="invite-archive-all"
                          disabled={derivedBusy || derivedUsers.filter((u) => u.status !== 'archived_soft_launch').length === 0}>
                    <Archive className="h-3.5 w-3.5 mr-1" /> Archive all
                  </Button>
                </div>
              </div>

              {derivedUsers.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">No users have used this invite yet.</p>
              ) : (
                <table className="min-w-full text-left text-xs" data-testid="invite-derived-table">
                  <thead className="text-xs text-slate-500 uppercase">
                    <tr>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Role</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Created at</th>
                      <th className="px-3 py-2 text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {derivedUsers.map((u) => (
                      <tr key={u.user_id} className="border-b border-slate-100" data-testid={`invite-derived-row-${u.user_id}`}>
                        <td className="px-3 py-2">{u.email}</td>
                        <td className="px-3 py-2">{u.name || '—'}</td>
                        <td className="px-3 py-2">{u.invite_allowed_role}</td>
                        <td className="px-3 py-2">
                          <span className={
                            u.status === 'archived_soft_launch'
                              ? 'px-2 py-0.5 rounded-full bg-slate-100 text-slate-600'
                              : 'px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium'
                          }>
                            {u.status === 'archived_soft_launch' ? 'archived' : (u.status || 'active')}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-500">{u.created_at}</td>
                        <td className="px-3 py-2 text-right">
                          {u.status === 'archived_soft_launch' && (
                            <Button size="sm" variant="outline" onClick={() => onUnarchiveOne(u.user_id)}
                                    data-testid={`invite-unarchive-${u.user_id}`}>
                              <RotateCcw className="h-3 w-3 mr-1" /> Unarchive
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
